/* global bocAmchart, AmCharts */

/**
 * @typedef {{
 *      bocSettings: {
 *          series,
 *          ga,
 *          addExport,
 *          lookup,
 *          args: {}
 *      },
 *      listeners,
 *      export,
 *      type,
 *      categoryAxis,
 *      chartScrollbar,
 *      titles
 * }} AMConfig
 */

/*
 * Polyfill for endsWith method not supported by IE
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/endsWith#Polyfill
 */
if (!String.prototype.endsWith) {
    String.prototype.endsWith = function (searchString, position) {
        var subjectString = this.toString();
        if (typeof position !== 'number' || !isFinite(position)
            || Math.floor(position) !== position || position > subjectString.length) {
            position = subjectString.length;
        }
        position -= searchString.length;
        var lastIndex = subjectString.indexOf(searchString, position);
        return lastIndex !== -1 && lastIndex === position;
    };
}

bocAmchart.theme = 'boc';

/**
 * Apply BOC settings to a chart.
 * @param {AMConfig} chartSettings The chart options
 * @returns {AMConfig} Modified chart options
 */
bocAmchart.applyBocSettings = function (chartSettings) {

    if (bocAmchart.debug(chartSettings)) {
        // eslint-disable-next-line no-console
        console.log(
            chartSettings.bocSettings.id,
            "Running applyBosSettings:",
            bocAmchart.debugClone(chartSettings)
        );
    }
    
    //If html has the data-theme set to dark make sure the charts are also dark
    if (document.documentElement.getAttribute('data-theme') === 'dark') {
        chartSettings.theme = 'bocDark';
        bocAmchart.theme = 'bocDark';
    } else {
        chartSettings.theme = 'boc';
    }
    // Need to define it early, so that early callback can use it
    chartSettings.bocSettings.theme = chartSettings.theme;

    chartSettings.listeners = chartSettings.listeners || [];

    if (chartSettings.bocSettings.args.hasOwnProperty("earlycallback")) {
        if (bocAmchart.debug(chartSettings)) {
            // eslint-disable-next-line no-console
            console.groupCollapsed(chartSettings.bocSettings.id + " running earlycallback " + chartSettings.bocSettings.args.earlycallback);
            // eslint-disable-next-line no-console
            console.log(window[chartSettings.bocSettings.args.earlycallback].toString());
            // eslint-disable-next-line no-console
            console.groupEnd();
        }
        window[chartSettings.bocSettings.args.earlycallback](chartSettings);
    }

    if (bocAmchart.debug(chartSettings)) {
        var events = [
            "init",
            "dataUpdated",
            "rendered",
            "drawn",
            "animationFinished",
            "buildStarted"
        ];
        events.forEach(function (eventName) {
            chartSettings.listeners.push({
                "event": eventName,
                "method": function (event) {
                    // eslint-disable-next-line no-console
                    console.groupCollapsed(event.chart.bocSettings.id + " triggered event " + eventName);
                    // eslint-disable-next-line no-console
                    console.trace();
                    // eslint-disable-next-line no-console
                    console.groupEnd();
                }
            });
        });
        chartSettings.listeners.push({
            "event": "resized",
            "method": function (event) {
                // eslint-disable-next-line no-console
                console.log(
                    chartSettings.bocSettings.id,
                    "chart triggered 'resized' event, new width: ",
                    event.chart.divRealWidth
                );
            }
        });
    }

    bocAmchart.addResizeUpdateListener(chartSettings);

    // Make changes to export menu and loaded resources
    if (chartSettings.bocSettings.addExport === true &&
        !chartSettings.bocSettings.args.hasOwnProperty('datasource')) {
        bocAmchart.updateExportMenu(chartSettings);
        if (bocAmchart.theme === 'bocDark') {
            chartSettings.export.backgroundColor = "#1a1820";
        }
    }

    if (chartSettings.bocSettings.args.hasOwnProperty('disablecursor')) {
        if (chartSettings.hasOwnProperty("chartCursor") && chartSettings.chartCursor !== null) {
            chartSettings.chartCursor = null;
        }
    }

    if (chartSettings.type === 'xy' && !chartSettings.bocSettings.args.hasOwnProperty('postprocessor')) {
        chartSettings.bocSettings.args.postprocessor = "postProcessValetXY";
    }

    if (chartSettings.type === 'serial') {
        var chartType;
        if (chartSettings.bocSettings.args.hasOwnProperty('type')) {
            chartType = chartSettings.bocSettings.args.type;
        } else {
            chartType = 'time';
        }

        if (chartType === 'text') {
            chartSettings.bocSettings.args.postprocessor = "postProcessValetCategories";
            chartSettings.categoryField = "category";
            chartSettings.categoryAxis.parseDates = false;
            if (chartSettings.hasOwnProperty("chartCursor") && chartSettings.chartCursor !== null) {
                chartSettings.chartCursor.categoryBalloonEnabled = false;
            }
        }

        if (chartType === 'waterfall') {
            chartSettings.bocSettings.args.postprocessor = "postProcessValetCategoriesWaterfall";
            chartSettings.categoryField = "category";
            chartSettings.categoryAxis.parseDates = false;
        }

        if (chartType === 'time') {
            // This is default time type, check if transpose is set to generate yield curve type charts
            if (chartSettings.bocSettings.args.hasOwnProperty('transpose')
                && chartSettings.bocSettings.args.transpose === "true") {
                chartSettings.bocSettings.args.postprocessor = "postProcessValetDateAsSeries";
                chartSettings.categoryField = "category";
                chartSettings.categoryAxis.parseDates = false;
            } else {
                bocAmchart.setMinPeriodAndCategoryBalloonFunction(chartSettings);
            }
        }

        if (chartType === 'relative') {
            chartSettings.bocSettings.args.postprocessor = "postProcessValetRelativeDateYear";
            chartSettings.categoryField = "date_year";
            chartSettings.categoryAxis.parseDates = false;
        }

        var chartStartDate, numberPart, endYear;
        if (typeof window.enddate !== 'undefined' && window.enddate !== '') {
            endYear = new Date(window.enddate).getFullYear();
        } else {
            endYear = new Date().getFullYear();
        }

        if (chartSettings.bocSettings.args.hasOwnProperty('datezoom')) {
            var zoomParam = chartSettings.bocSettings.args.datezoom;
            numberPart = zoomParam.slice(0, -1);
            chartStartDate = endYear - numberPart + "-01-01";
            if (zoomParam.slice(-1) === "Y" && parseInt(numberPart).toString() === numberPart) {
                bocAmchart.zoomFromDate(chartSettings, chartStartDate);
            }
        }

        if (chartSettings.bocSettings.args.hasOwnProperty('datecrop')) {
            var cropParam = chartSettings.bocSettings.args.datecrop;
            numberPart = cropParam.slice(0, -1);
            if (cropParam.slice(-1) === "Y" && parseInt(numberPart).toString() === numberPart) {
                chartStartDate = endYear - numberPart + "-01-01";
                bocAmchart.initChartVars(chartSettings);
                bocAmchart.vars[chartSettings.bocSettings.id]['startdateOverride'] = chartStartDate;
            }
        }
    }

    if (chartSettings.bocSettings.args.hasOwnProperty('rotatelabelsmaxwidth')) {
        chartSettings.responsive = {
            "enabled": true,
            "addDefaultRules": false,
            "rules": [{
                "maxWidth": chartSettings.bocSettings.args["rotatelabelsmaxwidth"],
                "overrides": {
                    "categoryAxis": {
                        "labelFunction": bocAmchart.wrapCategoryLabel10,
                        "labelRotation": 90
                    }
                }
            }]
        };
    }

    if (chartSettings.bocSettings.args.hasOwnProperty('rotate')) {
        chartSettings.rotate = true;
        if (chartSettings.hasOwnProperty("categoryAxis")) {
            chartSettings.categoryAxis.autoWrap = true;
            chartSettings.categoryAxis.ignoreAxisWidth = true;
            chartSettings.marginLeft = 200;
            if (chartSettings.hasOwnProperty("chartCursor") && chartSettings.chartCursor !== null) {
                chartSettings.chartCursor.categoryBalloonEnabled = false;
            }
        }
    }

    if (chartSettings.bocSettings.args.hasOwnProperty('hidelegend')
        && chartSettings.bocSettings.args.hidelegend  === "true") {
        chartSettings.legend = null;
    }

    if (chartSettings.hasOwnProperty('legend') && chartSettings.legend !== null) {

        /**
         * It's possible to extend this list as long as the parameters follow the same pattern and
         * are available as direct parameters on legend, exploratory
         */
        var legendAttributes = [
            "useGraphSettings",
            "labelWidth",
            "switchable",
            "periodValueText"
        ];

        var legendModified = false;

        legendAttributes.forEach(function (attr) {
            // AMCharts converts the properties to lowercase
            var legendProp = "legend" + attr.toLowerCase();
            if (chartSettings.bocSettings.args.hasOwnProperty(legendProp)) {
                switch (attr) {
                    case "switchable":
                    case "useGraphSettings":
                        if (chartSettings.bocSettings.args[legendProp] === "false") {
                            chartSettings.legend[attr] = false;
                        }
                        break;
                    case "periodValueText":
                        var unit = '';
                        if (typeof chartSettings.bocSettings.args.chartunit === 'string') {
                            unit = chartSettings.bocSettings.args.chartunit;
                        }
                        chartSettings.legend.valueText = "[[value]]" + unit;
                        chartSettings.legend.periodValueText = "[["
                            + chartSettings.bocSettings.args[legendProp] + "]]" + unit;
                        break;
                    default:
                        chartSettings.legend[attr] = chartSettings.bocSettings.args[legendProp];
                }

                if (bocAmchart.debug(chartSettings)) {
                    // eslint-disable-next-line no-console
                    console.log(
                        chartSettings.bocSettings.id,
                        "Setting legend " + attr + " based on shortcode: ",
                        chartSettings.bocSettings.args[legendProp]
                    );
                }
                legendModified = true;
            }
        });

        if (legendModified && bocAmchart.debug(chartSettings)) {
            // eslint-disable-next-line no-console
            console.log(
                chartSettings.bocSettings.id,
                "Modified legend: ",
                bocAmchart.debugClone(chartSettings.legend)
            );
        }
    }

    var axesById = [];
    if (chartSettings.hasOwnProperty("valueAxes") && chartSettings.valueAxes.length > 0) {
        chartSettings.valueAxes.forEach(function (axis) {
            axesById[axis.id] = axis;
        });

        /**
         * It's possible to extend this list as long as the parameters follow the same pattern and
         * are available as direct parameters on axis, exploratory
         */
        var axisAttributes = [
            "stackType",
            "unit",
            "reversed",
            "minimum",
            "maximum",
            "unitPosition"
        ];

        for (var property in chartSettings.bocSettings.args) {
            axisAttributes.forEach(function (attr) {
                if (chartSettings.bocSettings.args.hasOwnProperty(property)
                    && property.endsWith("axis" + attr.toLowerCase())) {
                    var axisId = property.slice(0, -(attr.length + 4));
                    if (axesById.hasOwnProperty(axisId)) {
                        axesById[axisId][attr] = chartSettings.bocSettings.args[property];
                        if (attr === "minimum" || attr === "maximum") {
                            axesById[axisId].strictMinMax = true;
                            chartSettings.synchronizeGrid = false;
                        }
                        if (bocAmchart.debug(chartSettings)) {
                            // eslint-disable-next-line no-console
                            console.log(
                                chartSettings.bocSettings.id,
                                "Setting " + axisId + " axis " + attr + " based on shortcode: ",
                                chartSettings.bocSettings.args[property]
                            );
                        }
                    }
                }
            });
        }
    }


    bocAmchart.setTitlesFromShortcode(chartSettings);

    chartSettings.language = window.chartLanguage;
    if (window.chartLanguage === 'fr') {
        chartSettings.decimalSeparator = ",";
        chartSettings.thousandsSeparator = " ";
    }

    if (chartSettings.hasOwnProperty("categoryAxis")) {
        if (chartSettings.bocSettings.args.hasOwnProperty("categoryaxislabelrotation")) {
            chartSettings.categoryAxis.labelRotation = chartSettings.bocSettings.args.categoryaxislabelrotation;
        }

        if (chartSettings.bocSettings.args.hasOwnProperty("categoryaxistitle")) {
            chartSettings.categoryAxis.title = chartSettings.bocSettings.args.categoryaxistitle;
        } else {
            if (chartSettings.bocSettings.args.hasOwnProperty("bottomaxistitle")) {
                chartSettings.categoryAxis.title = chartSettings.bocSettings.args.bottomaxistitle;
            }
        }
    }

    if (chartSettings.bocSettings.args.hasOwnProperty("categoryscrollbar")) {

        if (chartSettings.bocSettings.args.categoryscrollbar === "true") {
            chartSettings.chartScrollbar = {
                "autoGridCount": true,
                "oppositeAxis": false,
                "offset": 40,
                "color": AmCharts.themes[chartSettings.bocSettings.theme].AmCoordinateChart.colors[30]
            };
            if (bocAmchart.debug(chartSettings)) {
                // eslint-disable-next-line no-console
                console.log(
                    chartSettings.bocSettings.id,
                    "Adding category axis scroll bar based on shortcode",
                    ":",
                    bocAmchart.debugClone(chartSettings.chartScrollbar)
                );
            }
        } else {
            delete chartSettings.chartScrollbar;
            if (bocAmchart.debug(chartSettings)) {
                // eslint-disable-next-line no-console
                console.log(
                    chartSettings.bocSettings.id,
                    "Removing category axis scroll bar based on shortcode"
                );
            }
        }
    }

    if (bocAmchart.debug(chartSettings)) {
        // eslint-disable-next-line no-console
        console.log(
            chartSettings.bocSettings.id,
            "After applyBosSettings for",
            bocAmchart.debugClone(chartSettings)
        );
    }

    return chartSettings;
};


/**
 * Initialize the chart specific variables container based on chart's id
 * @param {AMConfig} chartSettings The chart options
 * @returns {void}
 */
bocAmchart.initChartVars = function (chartSettings) {
    if (!chartSettings.bocSettings.id) {
        // eslint-disable-next-line no-console
        console.log("Chart id not set for chart:", chartSettings.titles);
    }
    bocAmchart.vars = bocAmchart.vars || {};
    bocAmchart.vars[chartSettings.bocSettings.id] = bocAmchart.vars[chartSettings.bocSettings.id] || {};
};
